'use client';

import { SESSION_MERGE_EDUCATION_MODAL_ID } from '@/atoms/session-merge-onboarding-modal-ids';
import { useQueuedModal } from '@/hooks/useQueuedModal';
import { Button } from '@heidi/design-system/button';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { TypographyH2, TypographyP } from '@heidi/design-system/typography';
import { useAtom } from 'jotai';
import { useCallback, type ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import { showEnabledToastOnEducationCloseAtom } from '../../atoms';
import { useSessionMergeEnabledToast } from '../../hooks/useSessionMergeEnabledToast';
import { SessionMergeMatchingIllustration } from '../session-merge-matching-illustration';

/**
 * Instructional modal teaching the behaviour that makes auto-matching reliable
 * (Figma 9334-31452): say the patient's full name and date of birth at the
 * start of the session. Opened by the enable flow on every enable (see
 * `useShowSessionMergeInstructions`) and on demand from the "Session merge
 * enabled" toast's "Learn more" action. Acknowledging it — "Got it" or any
 * dismissal — is completion, not abandonment (the feature is already on), so
 * the enable flow shows the toast only after this closes, via
 * `showEnabledToastOnEducationCloseAtom`. A "Learn more" re-open never arms
 * that atom, so closing again shows no second toast (no toast ↔ modal loop).
 */
export function SessionMergeEducationModal(): ReactElement {
  const { isOpen, handleClose, dismissible } = useQueuedModal(
    SESSION_MERGE_EDUCATION_MODAL_ID
  );
  const [showToastOnClose, setShowToastOnClose] = useAtom(
    showEnabledToastOnEducationCloseAtom
  );
  const { showSessionMergeEnabledToast } = useSessionMergeEnabledToast();

  const handleAcknowledge = useCallback(() => {
    handleClose();
    if (showToastOnClose) {
      setShowToastOnClose(false);
      showSessionMergeEnabledToast();
    }
  }, [handleClose, showToastOnClose, setShowToastOnClose, showSessionMergeEnabledToast]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) handleAcknowledge();
    },
    [handleAcknowledge]
  );

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        className="flex h-[448px] max-h-[90vh] w-full max-w-4xl gap-0 overflow-hidden border-0 p-0 sm:flex-row"
        hideCloseIcon={false}
        dismissible={dismissible}
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="session-merge-education-modal"
      >
        <div className="flex min-w-0 flex-[1_1_50%] flex-col justify-center gap-6 overflow-y-auto bg-surface p-8">
          <div className="flex flex-col gap-4">
            <TypographyH2>
              <FormattedMessage
                defaultMessage="Heidi matches sessions to patients automatically"
                id="+Hj2F5AnDn"
              />
            </TypographyH2>
            <TypographyP className="text-text-secondary">
              <FormattedMessage
                defaultMessage="For best results, mention the patient's full name and date of birth at the start of the session."
                id="HghKzFbdod"
              />
            </TypographyP>
          </div>
          <div className="pt-6">
            <Button size="lg" className="w-full" onClick={handleAcknowledge}>
              <FormattedMessage defaultMessage="Got it" id="NYTGIbSNz5" />
            </Button>
          </div>
        </div>
        <div className="relative hidden min-w-0 flex-[1_1_50%] items-center justify-center overflow-hidden bg-surface-accent sm:flex">
          <SessionMergeMatchingIllustration />
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
